import type { FC, ReactNode } from "react";
import { createContext, useCallback, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import { authApi } from "src/api/auth";
import type { User } from "src/api/types/user";
import { Issuer } from "src/utils/auth";

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: User | null;
  clientDetails?: any;
}

enum ActionType {
  INITIALIZE = "INITIALIZE",
  SIGN_IN = "SIGN_IN",
  SIGN_UP = "SIGN_UP",
  SIGN_OUT = "SIGN_OUT",
}

type InitializeAction = {
  type: ActionType.INITIALIZE;
  payload: {
    isAuthenticated: boolean;
    user: User | null;
  };
};

type SignInAction = {
  type: ActionType.SIGN_IN;
  payload: {
    user: User;
    clientDetails?: any;
  };
};

type SignUpAction = {
  type: ActionType.SIGN_UP;
  payload: {
    user: User;
  };
};

type SignOutAction = {
  type: ActionType.SIGN_OUT;
};

type Action = InitializeAction | SignInAction | SignUpAction | SignOutAction;

type Handler = (state: State, action: any) => State;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers: Record<ActionType, Handler> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  SIGN_IN: (state: State, action: SignInAction): State => {
    console.log("SIGN_IN action received:", action);
    const { user, clientDetails } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
      clientDetails,
    };
  },
  SIGN_UP: (state: State, action: SignUpAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  SIGN_OUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state: State, action: Action): State =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export interface AuthContextType extends State {
  issuer: Issuer.JWT;
  signIn: (email: string, client_secret: string) => Promise<any>;
  signUp: (email: string, name: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  clientDetails?: any;
}

export const AuthContext = createContext<AuthContextType>({
  ...initialState,
  issuer: Issuer.JWT,
  signIn: () => Promise.resolve(),
  signUp: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async (): Promise<void> => {
    try {
      const user = await authApi.me();
      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: true,
          user,
        },
      });
    } catch (err) {
      console.error(err);

      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, []);

  useEffect(
    () => {
      initialize();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialize]
  );

  const signIn = useCallback(
    async (email: string, password: string): Promise<any> => {
      try {
        const response = await authApi.signIn({
          email,
          client_secret: password,
        });
        const clientDetails = response?.clientDetails;

        if (response.status) {
          const user = { 
            id: email, 
            email,
            client_id: response.clientDetails?.client_id || response.user?.client_id
          } as User;
          dispatch({
            type: ActionType.SIGN_IN,
            payload: { user, clientDetails },
          });
          return response;
        } else {
          throw new Error(response.message);
        }
      } catch (error) {
        console.error("Sign-in failed", error);
        throw error;
      }
    },
    []
  );

  const signUp = useCallback(
    async (email: string, name: string, password: string): Promise<void> => {
      try {
        await authApi.signUp({ email, name, client_secret: password });

        const user = await authApi.me();

        dispatch({
          type: ActionType.SIGN_UP,
          payload: { user },
        });
      } catch (error) {
        console.error("Sign-up failed", error);
        throw error;
      }
    },
    []
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
      // The backend will clear the auth cookie

      dispatch({ type: ActionType.SIGN_OUT });
      
    } catch (error) {
      console.error("Sign-out failed:", error);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        issuer: Issuer.JWT,
        signIn,
        signUp,
        signOut,
        clientDetails: state.clientDetails,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired as any,
};

export const AuthConsumer = AuthContext.Consumer;
